import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api, { signingAPI } from '../services/api';
import Button from './ui/Button';
import Input from './ui/Input';
import Alert from './ui/Alert';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';

const loadGoogleFonts = () => {
  if (document.getElementById('calligraphic-fonts')) return;
  const link = document.createElement('link');
  link.id = 'calligraphic-fonts';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Pacifico&display=swap';
  document.head.appendChild(link);
};

const SignatureSetupModal = ({
  onClose,
  onLater,
  onSuccess,
  isFirstTime = true,
  nameOverride = null,
  requireAdESPassword = false,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const canvasRef = useRef(null);
  const adesSectionRef = useRef(null);
  const [mode, setMode] = useState('draw');
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedFont, setSelectedFont] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userName, setUserName] = useState('');
  const [fontsLoaded, setFontsLoaded] = useState(false);
  // Folosit doar când `requireAdESPassword=true` — userul intră în același pas
  // parola contului ca să generăm cheia AdES după ce salvăm semnătura vizuală.
  const [adesPassword, setAdesPassword] = useState('');
  const [adesError, setAdesError] = useState(null);

  const fonts = [
    {
      name: 'Dancing Script',
      label: 'Elegant Clasic',
      cssFamily: "'Dancing Script', cursive",
      weight: '700',
      color: '#1a1a2e',
      fontSize: 58,
    },
    {
      name: 'Great Vibes',
      label: 'Cursiv Romantic',
      cssFamily: "'Great Vibes', cursive",
      weight: '400',
      color: '#0d1b2a',
      fontSize: 64,
    },
    {
      name: 'Pacifico',
      label: 'Rotunjit Modern',
      cssFamily: "'Pacifico', cursive",
      weight: '400',
      color: '#1b2838',
      fontSize: 48,
    },
  ];

  useEffect(() => {
    loadGoogleFonts();
    if (nameOverride) {
      setUserName(nameOverride);
    } else {
      fetchUserName();
    }
    document.fonts.ready.then(() => setFontsLoaded(true));
  }, [nameOverride]);

  const fetchUserName = async () => {
    try {
      const response = await api.get('/employees/me');
      setUserName(`${response.data.user.firstName} ${response.data.user.lastName}`);
    } catch (err) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setUserName(`${user.firstName || ''} ${user.lastName || ''}`.trim());
    }
  };

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    if (mode !== 'draw') return;
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.strokeStyle = isDark ? '#F8FAFC' : '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || mode !== 'draw') return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  // Enter de la tastatură salvează (la fel ca butonul „Salvează"). Sărim peste butoane
  // (Enter le activează deja nativ — ex. alegerea fontului) ca să nu dublăm acțiunea.
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || saving) return;
    if (e.target?.tagName === 'BUTTON') return;
    e.preventDefault();
    handleSave();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    // În modul caligrafic canvas-ul nu e montat — evităm accesul pe null.
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setSelectedFont(null);
  };

  // Modul caligrafic: doar reținem stilul ales (fără previzualizare). Semnătura se
  // randează pe un canvas alb abia la salvare (vezi handleSave).
  const buildGeneratedCanvas = (font) => {
    const c = document.createElement('canvas');
    c.width = 700;
    c.height = 200;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.font = `${font.weight} ${font.fontSize}px ${font.cssFamily}`;
    ctx.fillStyle = font.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(userName, c.width / 2, c.height / 2);
    return c;
  };

  // Scroll + focus pe câmpul de parolă (când userul încearcă să salveze fără parolă).
  const focusAdESPassword = () => {
    const section = adesSectionRef.current;
    if (!section) return;
    section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    section.querySelector('input')?.focus();
  };

  const handleSave = async () => {
    // Construim canvas-ul de export în funcție de mod (desenat vs. caligrafic generat).
    let exportCanvas;
    if (mode === 'generate') {
      if (!selectedFont) {
        showToast(t('modals.signatureSetup.chooseStyleFirst'), 'warning');
        return;
      }
      // Fără previzualizare, fontul nu e folosit nicăieri în DOM → browserul nu-l descarcă.
      // Îl forțăm explicit înainte de a-l randa pe canvas, altfel ar cădea pe fontul default.
      try {
        await document.fonts.load(
          `${selectedFont.weight} ${selectedFont.fontSize}px '${selectedFont.name}'`,
          userName
        );
      } catch {
        /* dacă eșuează, randăm cu ce e disponibil */
      }
      exportCanvas = buildGeneratedCanvas(selectedFont);
    } else {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const isEmpty = !imageData.data.some((channel) => channel !== 0);
      if (isEmpty) {
        showToast(t('modals.signatureSetup.drawFirst'), 'warning');
        return;
      }
      // Pentru PDF avem nevoie de semnătură închisă pe fundal alb.
      // În dark mode desenăm alb pe închis, deci inversăm pixelii la save.
      exportCanvas = canvas;
      if (isDark) {
        const tmp = document.createElement('canvas');
        tmp.width = canvas.width;
        tmp.height = canvas.height;
        const tctx = tmp.getContext('2d');
        tctx.fillStyle = '#FFFFFF';
        tctx.fillRect(0, 0, tmp.width, tmp.height);
        const src = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const dst = tctx.getImageData(0, 0, tmp.width, tmp.height);
        for (let i = 0; i < src.data.length; i += 4) {
          const a = src.data[i + 3];
          if (a === 0) continue; // neatins → rămâne alb
          // Luminozitatea pixelului desenat devine întunericul stroke-ului nostru.
          const lum = 0.2126 * src.data[i] + 0.7152 * src.data[i + 1] + 0.0722 * src.data[i + 2];
          const inv = 255 - lum;
          dst.data[i] = inv;
          dst.data[i + 1] = inv;
          dst.data[i + 2] = inv;
          dst.data[i + 3] = a;
        }
        tctx.putImageData(dst, 0, 0);
        exportCanvas = tmp;
      }
    }

    if (requireAdESPassword && !adesPassword) {
      setAdesError(t('modals.signatureSetup.adesPasswordRequired'));
      focusAdESPassword();
      return;
    }
    setAdesError(null);

    setSaving(true);
    try {
      const signatureData = exportCanvas.toDataURL('image/png');
      const signatureFont = selectedFont?.name ?? null;

      await api.post('/signatures/configure', {
        signatureData,
        signatureType: mode,
        signatureFont: mode === 'generate' ? signatureFont : null,
      });

      // Generăm cheia AdES în același pas dacă userul a introdus parola.
      // Eroarea AdES nu pierde semnătura vizuală — userul va fi redirecționat
      // separat la modalul de activare AdES la următorul check.
      if (requireAdESPassword) {
        try {
          await signingAPI.generate(adesPassword);
        } catch (err) {
          const msg = err.response?.data?.error || t('toasts.errorGeneric');
          setAdesError(msg);
          showToast(msg, 'error');
          return;
        }
      }

      showToast(t('modals.signatureSetup.saved'), 'success');
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.error || t('toasts.errorSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay overflow-y-auto" onKeyDown={handleKeyDown}>
      <div className="modal-content max-w-2xl my-8">
        {/* Header */}
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary-100 dark:bg-primary-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-primary-600 dark:text-primary-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {isFirstTime
                  ? t('modals.signatureSetup.titleFirst')
                  : t('modals.signatureSetup.titleUpdate')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('modals.signatureSetup.subtitle')}
              </p>
            </div>
          </div>
        </div>

        <div className="modal-body space-y-5">
          {/* Mode selector */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                setMode('draw');
                clearCanvas();
              }}
              className={`p-3 border-2 rounded-lg transition text-center ${mode === 'draw' ? 'border-primary-500 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
            >
              <svg
                className="w-6 h-6 mx-auto mb-1 text-slate-600 dark:text-slate-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
              <div className="font-medium text-sm text-slate-800 dark:text-slate-100">
                {t('modals.signatureSetup.modeDraw')}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {t('modals.signatureSetup.modeDrawDesc')}
              </div>
            </button>
            <button
              onClick={() => {
                setMode('generate');
                clearCanvas();
              }}
              className={`p-3 border-2 rounded-lg transition text-center ${mode === 'generate' ? 'border-primary-500 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'}`}
            >
              <svg
                className="w-6 h-6 mx-auto mb-1 text-slate-600 dark:text-slate-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                />
              </svg>
              <div className="font-medium text-sm text-slate-800 dark:text-slate-100">
                {t('modals.signatureSetup.modeGenerate')}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {t('modals.signatureSetup.modeGenerateDesc')}
              </div>
            </button>
          </div>

          {/* Canvas area — doar în modul „desenat". La caligrafic nu mai afișăm
                        previzualizare; semnătura se generează la salvare din stilul ales. */}
          {mode === 'draw' && (
            <div className="bg-surface-secondary rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {t('modals.signatureSetup.drawPrompt')}
                </span>
                <button
                  onClick={clearCanvas}
                  className="text-xs text-slate-500 dark:text-slate-400 hover:text-danger-600 dark:hover:text-danger-300 px-2 py-1 rounded hover:bg-danger-50 dark:hover:bg-danger-950/50 transition-colors"
                >
                  <svg
                    className="w-3.5 h-3.5 inline-block mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                  {t('modals.signatureSetup.clear')}
                </button>
              </div>
              <canvas
                ref={canvasRef}
                width={700}
                height={200}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                className="w-full rounded-md border border-slate-200 dark:border-slate-700 cursor-crosshair"
                style={{ touchAction: 'none', backgroundColor: isDark ? '#0F172A' : '#FFFFFF' }}
              />
              <p className="text-xs text-slate-400 dark:text-slate-400 mt-1.5 text-center">
                {t('modals.signatureSetup.drawHint')}
              </p>
            </div>
          )}

          {/* Font selector — fiecare opțiune arată numele angajatului scris în acel stil
                        caligrafic. Nu mai există căsuța albă separată de previzualizare (ocupa spațiu). */}
          {mode === 'generate' && (
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {t('modals.signatureSetup.chooseStyle')}
              </p>
              {!fontsLoaded ? (
                <div className="text-center py-4 text-slate-500 dark:text-slate-400 text-sm">
                  {t('modals.signatureSetup.loadingFonts')}
                </div>
              ) : (
                <div className="space-y-2">
                  {fonts.map((font) => (
                    <button
                      key={font.name}
                      onClick={() => setSelectedFont(font)}
                      className={`w-full p-3 border-2 rounded-lg text-left transition ${selectedFont?.name === font.name ? 'border-primary-500 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50' : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-surface-secondary'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                          {font.label}
                        </span>
                        {selectedFont?.name === font.name && (
                          <span className="text-primary-600 dark:text-primary-300 text-xs font-medium flex items-center gap-1">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            {t('common.select')}
                          </span>
                        )}
                      </div>
                      <div
                        className="text-center leading-tight"
                        style={{
                          fontFamily: font.cssFamily,
                          fontSize: `${Math.min(font.fontSize * 0.6, 38)}px`,
                          fontWeight: font.weight,
                          color: isDark ? '#F8FAFC' : font.color,
                        }}
                      >
                        {userName || t('common.name')}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* AdES password section — afișată în același pas dacă userul nu are încă cheie generată.
                        Chenar portocaliu + fundal accentuat ca să fie evident că trebuie introdusă parola. */}
          {requireAdESPassword && (
            <div
              ref={adesSectionRef}
              className="rounded-lg border-2 border-orange-400 dark:border-orange-600/70 bg-orange-50/70 dark:bg-orange-950/20 p-4"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-orange-600 dark:text-orange-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-200">
                    {t('modals.signatureSetup.adesHeader')}
                  </h3>
                  <p className="text-xs text-orange-800/80 dark:text-orange-200/70 mt-0.5">
                    {t('modals.signatureSetup.adesExplanation')}
                  </p>
                </div>
              </div>
              {adesError && (
                <Alert type="error" className="mb-3">
                  {adesError}
                </Alert>
              )}
              <Input
                type="password"
                label={t('modals.signatureSetup.adesPasswordLabel')}
                value={adesPassword}
                onChange={(e) => setAdesPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <p className="text-xs text-orange-800/70 dark:text-orange-200/60 mt-1.5">
                {t('modals.signatureSetup.adesPasswordHint')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {!isFirstTime && onClose && (
            <Button variant="secondary" size="md" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          )}
          {/* La onboarding (isFirstTime) oferim „Mai târziu" ca să nu blocăm platforma —
                        utilizatorul poate configura semnătura ulterior din profil. */}
          {isFirstTime && onLater && (
            <Button variant="secondary" size="md" onClick={onLater}>
              {t('common.later')}
            </Button>
          )}
          <Button
            variant="primary"
            size="md"
            loading={saving}
            onClick={handleSave}
            className="flex-1"
          >
            {saving ? t('modals.signatureSetup.saving') : t('modals.signatureSetup.save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignatureSetupModal;
